import { forwardRef } from '@hover/blueprint';
import React, { useCallback, useEffect, useState } from 'react';

import { instancesToMaterials } from 'src/modules/homeownerApp/components/Customize/SegmentationOverlay';
import { useImperativeTracking } from 'src/modules/homeownerApp/components/Customize/useImperativeTracking';
import { Web } from 'src/shared/constants/FeatureFlag';
import { useTreatment } from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

import type { PaintRegion } from '../../constants/customizationCategories';
import { warn } from '../../helpers/logging';
import { useHomeownerTranslate } from '../../hooks';
import { usePartnerCobranding } from '../../hooks/usePartner';
import type { DesignerPaintProduct } from '../../types';
import { isPaintProduct } from '../../types';
import { Palette } from '../Palette';
import { PaintColorPaletteSheet } from '../Palette/PaintColorPaletteSheet';

import type { CustomizeControlsProps } from './CustomizeControls';
import { CustomizeControls } from './CustomizeControls';
import {
  SubRegionPanel,
  SubRegionsPanels,
  usePaintSubRegions,
} from './SubRegions';
import { productTrackingData } from './tracking';
import {
  useCustomization,
  useCustomizationSegmentation,
} from './useCustomization';
import { usePalettes } from './usePalettes';

export interface CustomizePaintProps extends Partial<CustomizeControlsProps> {}

const CustomizePaint = forwardRef<CustomizePaintProps, 'div'>((props, ref) => {
  const t = useHomeownerTranslate();
  const {
    category,
    clearCustomization,
    customizeImage,
    customization,
    baseImage,
  } = useCustomization();
  const { subRegions } = usePaintSubRegions();
  const { selectedSegments, handlePlacementMode } =
    useCustomizationSegmentation();
  const { trackingRef } = useImperativeTracking();

  const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));

  const cobranding = usePartnerCobranding();

  const selectedRegion =
    category && category.type === 'paint' ? category.region : null;

  const {
    hasUserSelectedPalette,
    isLoading,
    isSelectPaletteOpen,
    onSelectPaletteOpen,
    onSelectPaletteClose,
    paintColors,
    selectedPalette,
    setSelectedPalette,
  } = usePalettes();

  const isMobile = useIsMobileSize();

  const [regionPalettes, setRegionPalettes] = useState<
    Record<PaintRegion, string | null>
  >(() =>
    subRegions.reduce(
      (acc, region) => ({ ...acc, [region]: null }),
      {} as Record<PaintRegion, null>,
    ),
  );

  const saveRegionPalette = useCallback(
    (palette: string | null) => {
      setRegionPalettes(existing => {
        if (!selectedRegion) return existing;

        return {
          ...existing,
          [selectedRegion]: palette,
        };
      });
    },
    [selectedRegion],
  );

  useEffect(() => {
    if (!customization) return;

    const { product, region } = customization;

    if (
      isPaintProduct(product) &&
      product.palettes.length > 0 &&
      category?.region === region &&
      !product.palettes.includes(selectedPalette)
    ) {
      saveRegionPalette(product.palettes[0]);
      setSelectedPalette(product.palettes[0]);
    }

    // Only rerun this effect when the customization changes. This ensures the
    // user can navigate to different palettes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customization, category?.region]);

  const regionPalette = selectedRegion ? regionPalettes[selectedRegion] : null;
  const currentPalette = regionPalette ?? selectedPalette;

  const handleSelectColor = (color: DesignerPaintProduct | null) => {
    if (color) {
      const materialClassesEdited =
        selectedSegments && handlePlacementMode === 'material'
          ? {
              material_classes_edited:
                instancesToMaterials(selectedSegments).length,
            }
          : undefined;

      trackingRef.current?.productSelected({
        ...productTrackingData(currentPalette, color),
        ...materialClassesEdited,
        edit_region: category?.region,
        edit_type: category?.type,
        selection_type: 'First Generation',
        ...(baseImage.fromSample && { track_id: 'edit_sample' }),
      });
      saveRegionPalette(currentPalette);
      void customizeImage(color);
    } else {
      clearCustomization();
    }
  };

  const handleSelectPaletteOpen = () => {
    trackingRef.current?.moduleViewed({
      edit_region: category?.region,
      edit_type: 'paint',
      track_id: `${
        baseImage.fromSample ? 'sample-' : ''
      }product-group-selection-module`,
    });
    onSelectPaletteOpen();
  };

  const handleSelectPalette = (palette: string) => {
    trackingRef.current?.buttonPressed({
      button_text: palette,
      edit_region: category?.region,
      edit_type: 'paint',
      primary_cta: false,
      product_group: palette,
      track_id: `${
        baseImage.fromSample ? 'sample-' : ''
      }product-group-selected`,
    });

    saveRegionPalette(null);
    setSelectedPalette(palette);
    onSelectPaletteClose();
  };

  const currentProduct = customization?.product;

  if (!category) {
    warn('CustomizePaint: attempted to render without a category');
    return null;
  }

  return (
    <CustomizeControls
      onSelectPaletteClick={handleSelectPaletteOpen}
      paletteLabel={regionPalette ?? selectedPalette}
      {...props}
      ref={ref}
    >
      <SubRegionsPanels>
        {subRegions.map(subRegion => (
          <SubRegionPanel key={subRegion}>
            <Palette
              category={category}
              itemProps={{
                borderRadius: '300',
                colorScheme: isMobile ? 'light' : 'dark',
              }}
              onSelectItem={handleSelectColor}
              selectedItem={
                isPaintProduct(currentProduct) &&
                category?.region === customization?.region
                  ? currentProduct
                  : null
              }
              selectedPalette={regionPalettes[subRegion] ?? selectedPalette}
              type="color"
            />
          </SubRegionPanel>
        ))}
      </SubRegionsPanels>
      <PaintColorPaletteSheet
        header={
          isNewProjectUiEnabled && cobranding && cobranding.type === 'paint'
            ? t('palette.cobrandCardHeader', {
                brandName: cobranding.displayName,
              })
            : t('palette.cardHeader')
        }
        isLoading={isLoading}
        isOpen={isSelectPaletteOpen}
        onClose={onSelectPaletteClose}
        onSelect={handleSelectPalette}
        paintColors={paintColors}
        selectedPalette={
          !hasUserSelectedPalette ? undefined : regionPalette ?? selectedPalette
        }
      />
    </CustomizeControls>
  );
});

export { CustomizePaint };
